import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageWrapper } from '../../components/layout';
import { RegisterTable, RegisterViews, RegisterPeek, useRegisterKeys } from '../../components/register';
import { LedgerSheet } from '../../components/ui';
import { RiskFormDialog } from '../../components/risks';
import { useToast } from '../../hooks/useToast';
import { MatrixSlice, RegisterParams, RegisterViewKey, useRiskDetail, useRisksPage } from '../../hooks/useRegisterQueries';
import { SavedView, loadSavedViews, saveSavedViews } from '../../lib/registerViews';
import { Risk } from '../../types/risk.types';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuth } from '../../hooks/useAuth';
import { cn } from '../../lib/cn';
import riskService from '../../services/risk.service';

const ORDERING_LABELS: Record<string, string> = {
  '-residual_score': 'residual ▾',
  residual_score: 'residual ▴',
  '-reference_number': 'number ▾',
  reference_number: 'number ▴',
  '-next_review_date': 'review ▾',
  next_review_date: 'review ▴',
};

// URL keys the dashboard matrix uses to deep-link a cell into the register.
const MATRIX_PARAM_KEYS = ['view', 'type', 'likelihood', 'impact'];

function matrixFromSearch(searchParams: URLSearchParams): MatrixSlice | undefined {
  if (searchParams.get('view') !== 'matrix') return undefined;
  const type = searchParams.get('type') === 'inherent' ? 'inherent' : 'residual';
  const likelihood = Number(searchParams.get('likelihood'));
  const impact = Number(searchParams.get('impact'));
  if (!likelihood || !impact) return undefined;
  return { type, likelihood, impact };
}

export const RisksPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { canCreateRisk, canExport } = usePermissions();
  const { user } = useAuth();
  const userId = user?.id ?? 'anon';

  const [view, setView] = useState<RegisterViewKey>('all');
  const [activeSavedId, setActiveSavedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [ordering, setOrdering] = useState('-residual_score');
  const [page, setPage] = useState(1);
  const [matrix, setMatrix] = useState<MatrixSlice | undefined>(() => matrixFromSearch(searchParams));
  // ?selected=<riskId> is the deep-link seam from the tasks screens' ↳ RSK refs.
  const [selectedId, setSelectedId] = useState<string | null>(() => searchParams.get('selected'));
  const [peekOpen, setPeekOpen] = useState(searchParams.has('selected'));
  const [dialogOpen, setDialogOpen] = useState(searchParams.get('create') === '1');
  // Captured object, not an id-into-current-rows derivation: a refetch that drops
  // the row from the loaded page mid-edit must not flip the dialog to create mode.
  const [editingRisk, setEditingRisk] = useState<Risk | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [savedViews, setSavedViews] = useState<SavedView[]>(() => loadSavedViews(userId));
  const toast = useToast();

  useEffect(() => {
    setSavedViews(loadSavedViews(userId));
  }, [userId]);

  // The palette's "c r" navigates to /risks?create=1 — also mid-session, when the
  // page is already mounted and the seed above never re-runs.
  useEffect(() => {
    if (searchParams.get('create') === '1') setDialogOpen(true);
  }, [searchParams]);

  const params: RegisterParams = useMemo(
    () => ({ view, matrix, search, ordering, page }),
    [view, matrix, search, ordering, page],
  );
  const { data, isPending, isError } = useRisksPage(params);
  const rows = data?.results ?? [];
  const count = data?.count ?? 0;

  useEffect(() => {
    if (isError) toast.error('Failed to load risks');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  // Strip only the given keys so the ?create=1 strip and the matrix strip can
  // never clobber each other's parameters.
  const stripParams = (keys: string[]) => {
    if (!keys.some((key) => searchParams.has(key))) return;
    const next = new URLSearchParams(searchParams);
    keys.forEach((key) => next.delete(key));
    setSearchParams(next, { replace: true });
  };

  // Strip the seeded ?selected once — back/refresh must not re-open the peek.
  useEffect(() => {
    stripParams(['selected']);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectView = (next: RegisterViewKey) => {
    setView(next);
    setActiveSavedId(null);
    setPage(1);
  };
  const selectSaved = (saved: SavedView) => {
    setView(saved.view);
    setSearch(saved.search);
    setOrdering(saved.ordering);
    setActiveSavedId(saved.id);
    setPage(1);
  };
  const saveView = (name: string) => {
    const next = [...savedViews, { id: crypto.randomUUID(), name, view, search, ordering }];
    setSavedViews(next);
    saveSavedViews(userId, next);
  };
  const deleteSaved = (id: string) => {
    const next = savedViews.filter((saved) => saved.id !== id);
    setSavedViews(next);
    saveSavedViews(userId, next);
    if (activeSavedId === id) setActiveSavedId(null);
  };
  const clearMatrix = () => {
    setMatrix(undefined);
    setPage(1);
    stripParams(MATRIX_PARAM_KEYS);
  };

  const selectedIndex = rows.findIndex((risk) => risk.id === selectedId);
  const selectedRisk = selectedIndex >= 0 ? rows[selectedIndex] : null;
  // A URL-seeded risk may not be on the loaded page; fetch it by id for the peek.
  const urlPeek = useRiskDetail(peekOpen && selectedRisk == null ? selectedId : null);
  const peekRisk = selectedRisk ?? urlPeek.data ?? null;
  const move = useCallback(
    (delta: number) => {
      if (rows.length === 0) return;
      const from = selectedIndex < 0 ? -delta : selectedIndex;
      const next = Math.min(rows.length - 1, Math.max(0, from + delta));
      setSelectedId(rows[next].id);
    },
    [rows, selectedIndex],
  );
  useRegisterKeys({
    enabled: !dialogOpen,
    onDown: () => move(1),
    onUp: () => move(-1),
    onPeek: () => setPeekOpen(selectedId != null),
    onClose: () => setPeekOpen(false),
  });

  const openCreate = () => {
    setEditingRisk(null);
    setDialogOpen(true);
  };
  const openEdit = (id: string) => {
    const risk = rows.find((row) => row.id === id) ?? (peekRisk?.id === id ? peekRisk : null);
    if (!risk) return;
    setEditingRisk(risk);
    setDialogOpen(true);
  };
  const closeDialog = () => {
    setDialogOpen(false);
    setEditingRisk(null);
    stripParams(['create']);
  };
  // The dialog saves through the register mutations, which invalidate the risk
  // query families themselves — success here only owns the snackbar.
  const handleFormSuccess = () => {
    // onSuccess fires before the dialog's own handleClose -> closeDialog resets
    // editingRisk, so the captured object still decides the message here.
    toast.success(editingRisk ? 'Risk updated' : 'Risk recorded');
  };

  // Deliberately unfiltered: the export is the full register, not the current
  // view/search/matrix slice (per plan).
  const exportRegister = async (format: 'csv' | 'pdf') => {
    try {
      const blob = await riskService.exportRisks(format);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `risk_register_${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    }
  };

  const sharedViewProps = {
    activeSavedId,
    savedViews,
    matrix,
    search,
    onSaveView: saveView,
    onDeleteSaved: deleteSaved,
    onClearMatrix: clearMatrix,
    onSearchChange: (value: string) => {
      setSearch(value);
      setPage(1);
    },
  };

  return (
    <PageWrapper
      title="Risk Register"
      subtitle={
        isPending
          ? undefined
          : `${count} risk${count === 1 ? '' : 's'} · sorted by ${ORDERING_LABELS[ordering] ?? ordering}`
      }
      noPaper
      maxWidth="xl"
      actions={
        <span className="flex items-baseline gap-3">
          {canExport && (
            <>
              <button type="button" onClick={() => exportRegister('csv')} className="cap-label cursor-pointer hover:!text-ink">
                Export CSV
              </button>
              <button type="button" onClick={() => exportRegister('pdf')} className="cap-label cursor-pointer hover:!text-ink">
                Export PDF
              </button>
            </>
          )}
          {canCreateRisk && (
            <button
              type="button"
              onClick={openCreate}
              className="cursor-pointer bg-accent px-3 py-1 text-scale-1 font-semibold text-paper hover:bg-accent-hover"
            >
              + Record risk
            </button>
          )}
        </span>
      }
    >
      <div className="hidden md:block">
        <RegisterViews {...sharedViewProps} active={view} onSelectView={selectView} onSelectSaved={selectSaved} />
      </div>
      <div className="flex justify-end md:hidden">
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={sheetOpen}
          className="cap-label cursor-pointer"
        >
          Filter <span aria-hidden>▾</span>
        </button>
      </div>
      <LedgerSheet open={sheetOpen} onOpenChange={setSheetOpen} title="Views & search">
        <RegisterViews
          {...sharedViewProps}
          active={view}
          onSelectView={(next) => {
            selectView(next);
            setSheetOpen(false);
          }}
          onSelectSaved={(saved) => {
            selectSaved(saved);
            setSheetOpen(false);
          }}
        />
      </LedgerSheet>

      <div className={cn('mt-1', peekOpen && peekRisk && 'lg:grid lg:grid-cols-[1fr_340px]')}>
        <div className="min-w-0">
          <RegisterTable
            rows={rows}
            count={count}
            page={page}
            hasNext={data?.next != null}
            hasPrevious={data?.previous != null}
            isPending={isPending}
            ordering={ordering}
            selectedId={selectedId}
            onSelect={(id) => {
              setSelectedId(id);
              setPeekOpen(true);
            }}
            onOrderingChange={(next) => {
              setOrdering(next);
              setPage(1);
            }}
            onPageChange={setPage}
            onResetView={() => {
              selectView('all');
              setSearch('');
              clearMatrix();
            }}
          />
        </div>
        {peekOpen && peekRisk && (
          <RegisterPeek risk={peekRisk} onClose={() => setPeekOpen(false)} onEdit={openEdit} />
        )}
      </div>

      <RiskFormDialog open={dialogOpen} risk={editingRisk} onClose={closeDialog} onSuccess={handleFormSuccess} />
    </PageWrapper>
  );
};
